import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getMarketData } from "../../services/allApiService";
import {
    isMarketSocketConnected,
    onTickBatch,
} from "../../services/socket/marketSocket";
import Footer from "../Footer.js";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Import custom hooks
import { useOrders } from "./hooks/useOrders";
import { useMarketData } from "./hooks/useMarketData";

// Import components
import OrderHeader from "./OrderHeader";
import OrderTabs from "./OrderTabs";
import OrderList from "./OrderList";
import MarketDataDialog from "./MarketDataDialog";
import { useDispatch, useSelector } from 'react-redux';
import { getFilteredOrders, normalizeExchange } from "./orderUtils";

function Order() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState("limit");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [marketData, setMarketData] = useState(null);
    // Show the full-screen loader only on the very first load, not on the
    // 1-second background refreshes (otherwise it would flicker every tick).
    const [initialLoad, setInitialLoad] = useState(true);

    // Custom hooks
    const { orders, limitOrders, error, loading } = useOrders();

    const { allMarketData } = useMarketData(activeTab === "limit" ? limitOrders : orders);

    // Once the first fetch settles, stop gating the list behind the loader.
    useEffect(() => {
        if (!loading && initialLoad) setInitialLoad(false);
    }, [loading, initialLoad]);

    // Handle Search Bar
    const handleSearchBar = () => {
        navigate("/search-bar");
    };

    // Handle tab change (limit / executed)
    const handleTabChange = (event, newValue) => {
        const tabNames = ["limit", "executed"];
        setActiveTab(tabNames[newValue]);
    };

    // Handle card click and fetch market data
    const handleCardClick = async (order) => {
        // debugger;
        if (activeTab === "limit") {
            try {
                const normalizedExchange = normalizeExchange(order.exchange);
                const mode = "FULL";
                const exchangeTokens = { [normalizedExchange]: [order.token] };
                // One-time snapshot for the last price (works when market closed).
                const response = await getMarketData(mode, exchangeTokens);
                const fetchedMarketData = response.data.fetched;
                navigate('/edit-order', {
                    state: {
                        limit_order_id: order.limit_order_id,
                        buttonType: "edit",
                        scriptName: order.script_name,
                        exchange: order.exchange,
                        price: order.price,
                        lotssize: order.lots,
                        token: order.token,
                        script_id: order.script_id,
                        exchange_id: order.exchange_id,
                        order_id: order.exchange_id,
                        squareOff: order.square_off,
                        qty: order.qty,
                        expiry: order.expiry,
                        type: order.type,
                        order_type: order.order_type,
                        orderId: order.parent_order_id,
                        marketData: fetchedMarketData
                    }
                });
            } catch (error) {
                console.error("Failed to fetch market data:", error);
            }
        }

        setSelectedOrder(order);
    };

    // Real-time market data updates for selected order
    useEffect(() => {
        let intervalId;
        let offTick = null;
        if (!selectedOrder) return;

        const targetToken = String(selectedOrder.token || selectedOrder.symbolToken);

        const fetchData = async () => {
            const normalizedExchange = normalizeExchange(selectedOrder.exchange);

            try {
                const mode = "FULL";
                const exchangeTokens = { [normalizedExchange]: [selectedOrder.token] };
                const response = await getMarketData(mode, exchangeTokens);
                setMarketData(response.data.fetched);
            } catch (error) {
                console.error("Failed to fetch market data:", error);
                setMarketData(null);
            }
        };

        (() => {
            // One-time snapshot so the dialog shows the LAST price immediately
            // (works even when the market is closed — NFO after 3:30 PM). The
            // socket then updates it live via onTickBatch below; the REST poll
            // only runs while the socket is down.
            fetchData();

            // Poll only while the socket is NOT delivering ticks.
            intervalId = setInterval(() => {
                if (isMarketSocketConnected()) return;
                fetchData();
            }, 1000);

            // Merge live ticks into the dialog's local state.
            offTick = onTickBatch((batch) => {
                const tick = batch.get(targetToken);
                if (!tick) return;
                setMarketData((prev) => {
                    if (!Array.isArray(prev) || prev.length === 0) {
                        return [{
                            symbolToken: targetToken,
                            tradingSymbol: tick.tradingSymbol || selectedOrder.script_name || '',
                            exchange: selectedOrder.exchange,
                            ltp: tick.ltp, high: tick.high, low: tick.low,
                            open: tick.open, close: tick.close,
                            avgPrice: tick.avgPrice, tradeVolume: tick.tradeVolume,
                            netChange: tick.netChange, percentChange: tick.percentChange,
                            upperCircuit: tick.upperCircuit, lowerCircuit: tick.lowerCircuit,
                            depth: tick.depth || { buy: [], sell: [] },
                        }];
                    }
                    return prev.map((row) => {
                        if (String(row.symbolToken) !== targetToken) return row;
                        return {
                            ...row,
                            tradingSymbol: row.tradingSymbol || tick.tradingSymbol,
                            ltp: tick.ltp ?? row.ltp,
                            high: tick.high ?? row.high,
                            low: tick.low ?? row.low,
                            open: tick.open ?? row.open,
                            close: tick.close ?? row.close,
                            avgPrice: tick.avgPrice ?? row.avgPrice,
                            tradeVolume: tick.tradeVolume ?? row.tradeVolume,
                            netChange: tick.netChange ?? row.netChange,
                            percentChange: tick.percentChange ?? row.percentChange,
                            upperCircuit: tick.upperCircuit ?? row.upperCircuit,
                            lowerCircuit: tick.lowerCircuit ?? row.lowerCircuit,
                            depth: tick.depth ?? row.depth,
                        };
                    });
                });
            });
        })();

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (offTick) offTick();
        };
    }, [selectedOrder]);

    // Handle order action (buy/sell)
    const handleOrderAction = (
        buttonType, tradingSymbol, exchange, price, lots,
        netChange, percentChange, symbolToken, script_id, exchange_id,
        order_id, square_off, qty, expiry, token, high, low,
        upperCircuit, lowerCircuit
    ) => {
        navigate('/add-order', {
            state: {
                o_buttonType: buttonType,
                o_scriptName: tradingSymbol,
                o_exchange: exchange,
                o_price: price,
                o_lotssize: lots,
                o_netChange: netChange,
                o_percentChange: percentChange,
                o_symbolToken: symbolToken,
                o_script_id: script_id,
                o_exchange_id: exchange_id,
                o_order_id: order_id,
                o_square_off: square_off,
                o_qty: qty,
                o_expiry: expiry,
                o_token: token,
                o_high: high,
                o_low: low,
                o_upperCircuit: upperCircuit,
                o_lowerCircuit: lowerCircuit
            },
        });
    };

    // Get filtered orders based on active tab
    const filteredOrders = getFilteredOrders(orders, limitOrders, activeTab);

    return (
        <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
            <div
                className="flex flex-col h-[100vh] max-w-sm min-w-[370px] w-full bg-gray-100">
                {/* Header */}
                <OrderHeader onSearchClick={handleSearchBar} />

                {/* Tabs */}
                <OrderTabs activeTab={activeTab} onTabChange={handleTabChange} />

                {loading && initialLoad && (
                    <div className="flex items-center justify-center min-h-screen">
                        <p>Loading orders...</p>
                    </div>
                )}
                {/* Orders List */}
                <div
                    className="flex-1 overflow-y-auto p-4
                [&::-webkit-scrollbar]:w-1 
                [&::-webkit-scrollbar-track]:bg-transparent 
                [&::-webkit-scrollbar-thumb]:bg-transparent 
                scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent"
                >
                    <OrderList
                        orders={filteredOrders}
                        allMarketData={allMarketData}
                        onCardClick={handleCardClick}
                        activeTab={activeTab}
                    />
                </div>
                {/* Market Data Dialog */}
                <MarketDataDialog
                    selectedOrder={selectedOrder}
                    marketData={marketData}
                    onClose={() => setSelectedOrder(null)}
                    onOrderAction={handleOrderAction}
                />
            </div>

            <Footer />
            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
}

export default Order;